/* ==================================================================
   Go For Gold · API layer
   Fetches the campaign payload (topFive endpoint) and maps ONLY the
   `resp.independence` object onto:
     - S      → the state shape the whole UI already reads
     - ind    → the raw independence object, for section-level UI data
   The UI keeps its static defaults until this data arrives, and on
   error / enabled=false nothing is applied.
   ================================================================== */
import * as APIHelper from '../Helper/apiFetch';

const HOUR = 3600000;
const DAY = 86400000;
const IST_OFFSET = 5.5 * HOUR;

/* hours left before midnight IST — today's SIP window */
function hoursLeftToday(now) {
  const ist = now + IST_OFFSET;
  const nextMidnight = (Math.floor(ist / DAY) + 1) * DAY;
  return Math.max(0, Math.ceil((nextMidnight - ist) / HOUR));
}

/* campaign phase from the API timestamps */
function phaseFrom(ind, now) {
  const { campaignStart, campaignEnd, joinEndsOn } = ind;
  if (campaignStart && now < campaignStart) return 'teaser';
  if (campaignEnd && now > campaignEnd) return 'ended';
  if (campaignEnd && campaignEnd - now <= 48 * HOUR) return 'final48';
  if (joinEndsOn && now <= joinEndsOn) return joinEndsOn - now <= DAY ? 'join_last' : 'join_open';
  return 'join_closed';
}

/* maps resp.independence → { S, ind }, or null when unusable */
export function mapIndependence(resp, now = Date.now()) {
  const data = resp && (resp.data ?? resp);
  const ind = data && data.independence;
  if (!ind || !ind.enabled) return null;

  const streak = ind.streak || null;
  const days = (streak && streak.card && streak.card.days) || [];
  const today = days.find((d) => d.isToday);
  const claim = ind.claim || {};

  const S = {
    phase: phaseFrom(ind, now),
    joined: !!streak && (streak.status === 'ACTIVE' || streak.status === 'BROKEN' || streak.status === 'COMPLETED'),
    done: streak ? streak.sipsDone || 0 : 0,
    window: streak ? streak.windowDays || days.length || 7 : 7,
    missed: streak ? streak.skipsUsed || 0 : 0,
    todayDone: !!(today && today.done),
    hoursLeft: hoursLeftToday(now),
    broken: !!streak && streak.status === 'BROKEN',
    claim: claim.available ? 'claimable' : 'none',
    hasCard: !!(ind.voucher && ind.voucher.eligible),
    invested: streak ? streak.cumulativeAmount || 0 : 0,
    daysToJoin: ind.joinEndsOn ? Math.max(-1, Math.ceil((ind.joinEndsOn - now) / DAY)) : -1,
    daysToEnd: ind.campaignEnd ? Math.max(0, Math.ceil((ind.campaignEnd - now) / DAY)) : 0,
    /* API-sourced values that override static fallbacks where present */
    apiRewardInr: streak && streak.rewardInr != null ? streak.rewardInr : null,
    startsOn: ind.campaignStart || null,
    joinEndsOn: ind.joinEndsOn || null,
    endsOn: ind.campaignEnd || null,
  };
  return { S, ind };
}

export async function fetchIndependence() {
  const res = await APIHelper.topFive();
  if (!res || res.error) return null;
  return mapIndependence(res);
}

/* POST v1/campaign/independence/claim — claims the streak reward */
export async function claimIndependence() {
  return APIHelper.independenceClaim();
}
